import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  FaExchangeAlt,
  FaSearch,
  FaPaperPlane,
  FaComments,
} from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import { format } from "date-fns";
import { hitBeApi } from "../../../api/api";
import {
  useAiFeatureColors,
  AiFeatureGuidePanel,
  AiHeroCard,
  AiStepStrip,
  AiHistorySidebar,
  AiHistoryItem,
} from "../ai/aiFeatureUi";

const fmtWhen = (d) => (d ? format(new Date(d), "EEE, d MMM yyyy · h:mm a") : "");

export default function TransferAi() {
  const toast = useToast();
  const { pageBg, cardBg, borderColor, muted, panelBg, accent } =
    useAiFeatureColors("teal");

  // ---- my posts --------------------------------------------------------------
  const [myPosts, setMyPosts] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);

  // ---- post form -------------------------------------------------------------
  const [fromCity, setFromCity] = useState("");
  const [toCity, setToCity] = useState("");
  const [travelAt, setTravelAt] = useState("");
  const [notes, setNotes] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [publishing, setPublishing] = useState(false);

  // ---- search ----------------------------------------------------------------
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null); // null = not searched yet

  // ---- chat ------------------------------------------------------------------
  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  /** Post we are composing a first message for (no conversation yet). */
  const [composeFor, setComposeFor] = useState(null);
  const [chatText, setChatText] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);
  const messagesBoxRef = useRef(null);

  const loadMyPosts = useCallback(async () => {
    setLoadingMine(true);
    try {
      const res = await hitBeApi("transfer-ai/posts/mine", "GET");
      setMyPosts(res?.data?.posts || []);
    } catch (error) {
      toast({
        title: "Could not load your posts",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingMine(false);
    }
  }, [toast]);

  useEffect(() => {
    loadMyPosts();
  }, [loadMyPosts]);

  const loadChats = useCallback(async () => {
    setLoadingChats(true);
    try {
      const res = await hitBeApi("transfer-ai/conversations", "GET");
      setChats(res?.data?.conversations || []);
    } catch {
      /* silent — chats card shows empty state */
    } finally {
      setLoadingChats(false);
    }
  }, []);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  // Poll the open conversation so replies appear without a manual refresh.
  useEffect(() => {
    if (!chatOpen || !activeChat?._id) return undefined;
    const id = activeChat._id;
    const interval = setInterval(async () => {
      try {
        const res = await hitBeApi(`transfer-ai/conversations/${id}`, "GET");
        if (res?.data?.conversation) setActiveChat(res.data.conversation);
      } catch {
        /* keep showing the last loaded state */
      }
    }, 12000);
    return () => clearInterval(interval);
  }, [chatOpen, activeChat?._id]);

  // Keep the message list scrolled to the newest message.
  useEffect(() => {
    const el = messagesBoxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeChat?.messages?.length, chatOpen]);

  const openChatForPost = (post) => {
    setComposeFor(post);
    setActiveChat(null);
    setChatText(
      `Hi${post.poster?.name ? ` ${post.poster.name}` : ""}! I saw you're travelling ${post.fromCity} → ${post.toCity} on ${fmtWhen(post.travelAt)}. Could you help me by bringing a small item along? I'll share the details here.`
    );
    setChatOpen(true);
  };

  const openExistingChat = async (conversationId) => {
    setComposeFor(null);
    setActiveChat(null);
    setChatText("");
    setChatOpen(true);
    try {
      const res = await hitBeApi(
        `transfer-ai/conversations/${conversationId}`,
        "GET"
      );
      if (res?.data?.conversation) {
        setActiveChat(res.data.conversation);
        loadChats(); // refresh unread badges
      }
    } catch (error) {
      toast({
        title: "Could not open chat",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setChatOpen(false);
    }
  };

  const sendChatMessage = async () => {
    const text = chatText.trim();
    if (!text || sendingMsg) return;
    setSendingMsg(true);
    try {
      let res;
      if (activeChat?._id) {
        res = await hitBeApi(
          `transfer-ai/conversations/${activeChat._id}/messages`,
          "POST",
          { message: text }
        );
      } else if (composeFor?._id) {
        res = await hitBeApi("transfer-ai/conversations", "POST", {
          postId: composeFor._id,
          message: text,
        });
        setComposeFor(null);
      } else {
        return;
      }
      if (res?.data?.conversation) setActiveChat(res.data.conversation);
      setChatText("");
      loadChats();
    } catch (error) {
      toast({
        title: "Could not send message",
        description: error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSendingMsg(false);
    }
  };

  const publishPost = async () => {
    if (publishing) return;
    if (!fromCity.trim() || !toCity.trim() || !travelAt) {
      toast({
        title: "Missing details",
        description: "From city, to city, and travel date & time are required.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setPublishing(true);
    try {
      await hitBeApi("transfer-ai/posts", "POST", {
        fromCity: fromCity.trim(),
        toCity: toCity.trim(),
        travelAt: new Date(travelAt).toISOString(),
        notes: notes.trim(),
        contactInfo: contactInfo.trim(),
      });
      toast({
        title: "Travel post published",
        description: "Anyone searching these cities and date can now find you.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setFromCity("");
      setToCity("");
      setTravelAt("");
      setNotes("");
      setContactInfo("");
      loadMyPosts();
    } catch (error) {
      toast({
        title: "Could not publish",
        description: error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setPublishing(false);
    }
  };

  const deletePost = async (id) => {
    try {
      await hitBeApi(`transfer-ai/posts/${id}`, "DELETE");
      setMyPosts((prev) => prev.filter((p) => p._id !== id));
    } catch (error) {
      toast({
        title: "Could not delete post",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const runSearch = async () => {
    if (searching) return;
    if (!searchFrom.trim() && !searchTo.trim() && !searchDate) {
      toast({
        title: "Empty search",
        description: "Type a from/to city (even part of it) or pick a date.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchFrom.trim()) params.set("from", searchFrom.trim());
      if (searchTo.trim()) params.set("to", searchTo.trim());
      if (searchDate) params.set("date", searchDate);
      const res = await hitBeApi(
        `transfer-ai/search?${params.toString()}`,
        "GET"
      );
      setResults(res?.data?.posts || []);
    } catch (error) {
      toast({
        title: "Search failed",
        description: error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSearching(false);
    }
  };

  return (
    <Box minH="100vh" bg={pageBg} p={{ base: 3, md: 6 }}>
      <AiFeatureGuidePanel
        title="Transfer AI"
        intro="A travel board for the Manage360 community. Post when you are travelling between cities, and search who else is travelling on a route and date — even a few letters of the city work."
        helps={[
          'Post your travel: "Delhi → Bangalore on 12 Jul, 6:30 pm" with optional notes and contact info.',
          'Search by route and calendar date — substrings match, so "del" finds Delhi and "bang" finds Bangalore.',
          "Results show the traveller's name, exact date & time, notes, and whatever contact they chose to share.",
          "Your own posts are listed on the left — delete them anytime.",
        ]}
        theme="teal"
      />

      <AiStepStrip
        theme="teal"
        steps={[
          {
            label: "Post your travel",
            hint: "From, to, and when — plus optional notes and contact.",
          },
          {
            label: "Search a route & date",
            hint: 'Substrings work: "del" → Delhi, "bang" → Bangalore.',
          },
          {
            label: "Connect",
            hint: "See who matches and reach out via their shared contact.",
          },
        ]}
      />

      <Flex gap={5} direction={{ base: "column", lg: "row" }} align="start">
        <AiHistorySidebar
          theme="teal"
          title="My travel posts"
          newLabel="Posts appear to searchers"
          isNewActive={false}
          onNew={() => {}}
          onRefresh={loadMyPosts}
          loading={loadingMine}
          emptyText="No travel posts yet. Publish your first one from the form on the right."
        >
          {myPosts.map((p) => (
            <AiHistoryItem
              key={p._id}
              theme="teal"
              active={false}
              title={`${p.fromCity} → ${p.toCity}`}
              meta={
                <Text fontSize="2xs" color={muted}>
                  {fmtWhen(p.travelAt)}
                </Text>
              }
              preview={p.notes}
              onSelect={() => {}}
              onDelete={(e) => {
                e.stopPropagation();
                deletePost(p._id);
              }}
            />
          ))}
        </AiHistorySidebar>

        <Box flex="1" w="full" minW={0}>
          <AiHeroCard
            theme="teal"
            title="Transfer AI"
            subtitle="Find who is travelling on your route — search by city (substrings work) and date."
          >
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
              <Input
                placeholder='From city — e.g. "del"'
                value={searchFrom}
                onChange={(e) => setSearchFrom(e.target.value)}
                bg="whiteAlpha.900"
                color="gray.800"
                _placeholder={{ color: "gray.500" }}
              />
              <Input
                placeholder='To city — e.g. "bang"'
                value={searchTo}
                onChange={(e) => setSearchTo(e.target.value)}
                bg="whiteAlpha.900"
                color="gray.800"
                _placeholder={{ color: "gray.500" }}
              />
              <Input
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                bg="whiteAlpha.900"
                color="gray.800"
              />
            </SimpleGrid>
            <Button
              leftIcon={<FaSearch />}
              colorScheme="whiteAlpha"
              bg="whiteAlpha.300"
              _hover={{ bg: "whiteAlpha.400" }}
              color="white"
              onClick={runSearch}
              isLoading={searching}
              loadingText="Searching"
            >
              Search travellers
            </Button>
          </AiHeroCard>

          {results !== null ? (
            <Box
              mt={5}
              bg={cardBg}
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="lg"
              p={{ base: 4, md: 6 }}
            >
              <Heading size="sm" mb={3}>
                {results.length
                  ? `${results.length} traveller${results.length === 1 ? "" : "s"} found`
                  : "No travellers found"}
              </Heading>
              {results.length ? (
                <VStack align="stretch" spacing={3}>
                  {results.map((p) => (
                    <Box
                      key={p._id}
                      bg={panelBg}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor={borderColor}
                      p={4}
                    >
                      <HStack justify="space-between" flexWrap="wrap" mb={1}>
                        <HStack spacing={2} minW={0}>
                          <Badge colorScheme={accent} borderRadius="full" px={2}>
                            {p.fromCity} → {p.toCity}
                          </Badge>
                          <Text fontSize="sm" fontWeight="semibold">
                            {p.poster?.name || "Manage360 user"}
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color={muted}>
                          {fmtWhen(p.travelAt)}
                        </Text>
                      </HStack>
                      {p.notes ? (
                        <Text fontSize="sm" lineHeight="tall" mb={1}>
                          {p.notes}
                        </Text>
                      ) : null}
                      <HStack
                        spacing={3}
                        flexWrap="wrap"
                        justify="space-between"
                      >
                        <HStack spacing={3} flexWrap="wrap">
                          {p.contactInfo ? (
                            <Text fontSize="xs" color={muted}>
                              Contact: {p.contactInfo}
                            </Text>
                          ) : null}
                          {p.poster?.email ? (
                            <Text fontSize="xs" color={muted}>
                              Email: {p.poster.email}
                            </Text>
                          ) : null}
                        </HStack>
                        <Button
                          size="sm"
                          leftIcon={<FaComments />}
                          colorScheme={accent}
                          onClick={() => openChatForPost(p)}
                        >
                          Chat & request
                        </Button>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="sm" color={muted}>
                  Nobody has posted this route/date yet. Try fewer letters of
                  the city, another date, or post your own travel below so
                  others can find you.
                </Text>
              )}
            </Box>
          ) : null}

          <Box
            mt={5}
            bg={cardBg}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="lg"
            p={{ base: 4, md: 6 }}
          >
            <HStack justify="space-between" mb={4} flexWrap="wrap">
              <HStack spacing={2}>
                <FaComments />
                <Heading size="sm">My chats</Heading>
              </HStack>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label="Refresh chats"
                icon={<FiRefreshCw />}
                onClick={loadChats}
                isLoading={loadingChats}
              />
            </HStack>
            {chats.length ? (
              <VStack align="stretch" spacing={2}>
                {chats.map((c) => (
                  <Box
                    key={c._id}
                    bg={panelBg}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={borderColor}
                    p={3}
                    cursor="pointer"
                    _hover={{ borderColor: `${accent}.300` }}
                    onClick={() => openExistingChat(c._id)}
                  >
                    <HStack justify="space-between" flexWrap="wrap">
                      <HStack spacing={2} minW={0}>
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                          {c.otherUser?.name || "Manage360 user"}
                        </Text>
                        {c.post ? (
                          <Badge
                            colorScheme={accent}
                            variant="subtle"
                            fontSize="2xs"
                          >
                            {c.post.fromCity} → {c.post.toCity}
                          </Badge>
                        ) : null}
                        {c.iAmPoster ? (
                          <Badge
                            colorScheme="orange"
                            variant="subtle"
                            fontSize="2xs"
                          >
                            about my post
                          </Badge>
                        ) : null}
                        {c.unreadCount > 0 ? (
                          <Badge colorScheme="red" borderRadius="full">
                            {c.unreadCount} new
                          </Badge>
                        ) : null}
                      </HStack>
                      <Text fontSize="xs" color={muted}>
                        {c.lastMessageAt ? fmtWhen(c.lastMessageAt) : ""}
                      </Text>
                    </HStack>
                    {c.lastMessagePreview ? (
                      <Text fontSize="xs" color={muted} noOfLines={1} mt={1}>
                        {c.lastMessagePreview}
                      </Text>
                    ) : null}
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text fontSize="sm" color={muted}>
                No chats yet. Search a route and use "Chat & request" on a
                traveller — or wait for someone to message you about your
                posts.
              </Text>
            )}
          </Box>

          <Box
            mt={5}
            bg={cardBg}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="lg"
            p={{ base: 4, md: 6 }}
          >
            <HStack mb={4} spacing={2}>
              <FaExchangeAlt />
              <Heading size="sm">Post your travel</Heading>
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">From city</FormLabel>
                <Input
                  placeholder="Delhi"
                  value={fromCity}
                  onChange={(e) => setFromCity(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">To city</FormLabel>
                <Input
                  placeholder="Bangalore"
                  value={toCity}
                  onChange={(e) => setToCity(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Travel date & time</FormLabel>
                <Input
                  type="datetime-local"
                  value={travelAt}
                  onChange={(e) => setTravelAt(e.target.value)}
                />
              </FormControl>
            </SimpleGrid>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Notes (optional)</FormLabel>
              <Textarea
                placeholder="e.g. Taking the 6:30 pm IndiGo flight, can carry a small parcel."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel fontSize="sm">
                Contact to share (optional — visible to searchers)
              </FormLabel>
              <Input
                placeholder="Phone / WhatsApp / Telegram"
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
              />
            </FormControl>
            <Button
              leftIcon={<FaPaperPlane />}
              colorScheme={accent}
              onClick={publishPost}
              isLoading={publishing}
              loadingText="Publishing"
            >
              Publish travel post
            </Button>
            <Text fontSize="xs" color={muted} mt={2}>
              Your name{contactInfo.trim() ? ", contact," : ""} and this post's
              details will be visible to anyone who searches a matching route
              or date.
            </Text>
          </Box>
        </Box>
      </Flex>

      <Modal
        isOpen={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setActiveChat(null);
          setComposeFor(null);
          setChatText("");
        }}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader pr={10}>
            {(() => {
              const post = activeChat?.post || composeFor;
              const name =
                activeChat?.otherUser?.name ||
                composeFor?.poster?.name ||
                "Traveller";
              return (
                <VStack align="start" spacing={0}>
                  <Text fontSize="md">{name}</Text>
                  {post ? (
                    <Text fontSize="xs" color={muted} fontWeight="normal">
                      {post.fromCity} → {post.toCity} · {fmtWhen(post.travelAt)}
                    </Text>
                  ) : null}
                </VStack>
              );
            })()}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {activeChat ? (
              <VStack
                align="stretch"
                spacing={2}
                ref={messagesBoxRef}
                maxH="45vh"
                overflowY="auto"
                pr={1}
              >
                {(activeChat.messages || []).map((m) => (
                  <Flex
                    key={m._id}
                    justify={m.mine ? "flex-end" : "flex-start"}
                  >
                    <Box
                      bg={m.mine ? `${accent}.500` : panelBg}
                      color={m.mine ? "white" : undefined}
                      borderWidth={m.mine ? 0 : "1px"}
                      borderColor={borderColor}
                      borderRadius="xl"
                      px={3}
                      py={2}
                      maxW="80%"
                    >
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {m.text}
                      </Text>
                      <Text
                        fontSize="2xs"
                        opacity={0.7}
                        textAlign="right"
                        mt={1}
                      >
                        {m.createdAt
                          ? format(new Date(m.createdAt), "d MMM, h:mm a")
                          : ""}
                      </Text>
                    </Box>
                  </Flex>
                ))}
                {!(activeChat.messages || []).length ? (
                  <Text fontSize="sm" color={muted} textAlign="center" py={4}>
                    No messages yet — say hello!
                  </Text>
                ) : null}
              </VStack>
            ) : composeFor ? (
              <Text fontSize="sm" color={muted}>
                Start the conversation — edit the message below and send. Ask
                them to bring something along, agree on details, and check back
                here for their reply.
              </Text>
            ) : (
              <Flex justify="center" py={8}>
                <Spinner color={`${accent}.500`} />
              </Flex>
            )}
          </ModalBody>
          <ModalFooter as={VStack} align="stretch" spacing={2}>
            <Textarea
              placeholder="Type your message…"
              value={chatText}
              onChange={(e) => setChatText(e.target.value)}
              rows={3}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendChatMessage();
                }
              }}
            />
            <HStack justify="flex-end">
              <Button
                leftIcon={<FaPaperPlane />}
                colorScheme={accent}
                onClick={sendChatMessage}
                isLoading={sendingMsg}
                loadingText="Sending"
                isDisabled={!chatText.trim() || (!activeChat && !composeFor)}
              >
                Send
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
